import React, { useState, useRef, useEffect, DragEvent, ChangeEvent } from "react";
import { CloudUpload, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { cn } from "@reactive-resume/utils";
import { Button } from "./button";

interface FileUploadProps {
  accept?: string;
  onChange: (file: File) => void;
  className?: string;
  label?: string;
  subtitle?: string;
  loading?: boolean;
  error?: string | null;
  hovered?: boolean;
}

type HookOptions = {
  accept?: string;
  onDrag: (isDragging: boolean) => void;
  onChoose: (file: File) => void;
  onError: (error: string) => void;
};

export const useFileDragDrop = ({ accept, onDrag, onChoose, onError }: HookOptions) => {
  const isDraggingTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onDrag(true);

    clearTimeout(isDraggingTimerRef.current);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingTimerRef.current = setTimeout(() => {
      onDrag(false);
    }, 100);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onDrag(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (accept) {
        const fileTypes = accept.split(",").map((type) => type.trim());
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        const fileType = file.type;

        const isAccepted = fileTypes.some((type) => {
          if (type.startsWith(".")) {
            return `.${fileExtension}` === type;
          } else if (type.endsWith("/*")) {
            const category = type.split("/")[0];
            return fileType.startsWith(category);
          }
          return fileType === type;
        });

        if (!isAccepted) {
          onError("File type not accepted");
          return;
        }
      }
      onChoose(file);
    }
  };

  return {
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
};

export const FileUpload = ({
  accept,
  onChange,
  className,
  label = "Choose File",
  subtitle = "or drag and drop",
  loading = false,
  error = null,
  hovered,
}: FileUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File>();
  const [internalError, setInternalError] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { handleDragOver, handleDragLeave, handleDrop } = useFileDragDrop({
    accept,
    onDrag: setIsDragging,
    onChoose: (file) => {
      setInternalError(null); // Clear internal error when new file is chosen
      onChange(file);
      setFile(file);
    },
    onError: (error) => {
      setInternalError(error);
      console.error(error);
    },
  });

  const handleClick = () => {
    if (loading) return; // Prevent interaction when loading
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || loading) return;
    const file = e.target.files[0];
    setInternalError(null); // Clear internal error when new file is chosen
    onChange(file);
    setFile(file);
  };

  useEffect(() => () => setFile(undefined), []);

  const displayError = error || internalError;
  const isDisabled = loading;
  const hasError = Boolean(displayError);
  const hasFile = Boolean(file);

  const effectiveHovered = typeof hovered === 'boolean' ? hovered : isHovered;

  const getBackgroundColor = () => {
    if (hasError) return "bg-red-50";
    if (isDragging) return "bg-primary/5";
    if (hasFile && !hasError) return "bg-green-50";
    return "";
  };

  const getIconColor = () => {
    if (hasError) return "text-red-500";
    if (isDragging) return "text-blue-500";
    if (hasFile && !hasError) return "text-green-500";
    return "text-gray-500";
  };

  const getButtonColor = () => {
    if (hasError) return "bg-red-500 hover:bg-red-600";
    if (isDragging) return "bg-blue-500 hover:bg-blue-600";
    if (hasFile && !hasError) return "bg-green-500 hover:bg-green-600";
  };

  const strokeColor = hasFile
    ? "#22c55e"
    : hasError
      ? "#ef4444"
      : isDragging
        ? "#3b82f6"
        : effectiveHovered
          ? "#000"
          : "#d1d5db";
  const duration = hasFile || hasError ? "8s" : effectiveHovered ? "0.6s" : "1s";

  const encodedSvg = encodeURIComponent(`
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
    <rect
      width="100%"
      height="100%"
      fill="none"
      rx="15"
      ry="15"
      stroke="${strokeColor}"
      stroke-width="3"
      stroke-dasharray="13"
      stroke-dashoffset="0"
    >
      <animate
        attributeName="stroke-dashoffset"
        values="0;26"
        dur="${duration}"
        repeatCount="indefinite"
      />
    </rect>
  </svg>
`);

  const backgroundImage = `url("data:image/svg+xml,${encodedSvg}")`;

  return (
    <div className="space-y-2">
      {/* Error message display */}
      {displayError && (
        <div className="flex items-center space-x-2 rounded-md border border-red-200 bg-red-50 p-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{displayError}</p>
        </div>
      )}
      <div
        style={{
          backgroundImage,
          borderRadius: 15,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
        }}
        className={cn(
          "group relative rounded-md p-6 transition-all duration-200",
          getBackgroundColor(),
          isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          className,
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onDragOver={isDisabled ? undefined : handleDragOver}
        onDragLeave={isDisabled ? undefined : handleDragLeave}
        onDrop={isDisabled ? undefined : handleDrop}
        onClick={isDisabled ? undefined : handleClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept={accept}
          onChange={handleFileChange}
          disabled={isDisabled}
        />
        <div className="flex flex-col items-center justify-center space-y-3">
          <div className={cn("rounded-md p-4")}>
            {loading ? (
              <Loader2 className="h-10 w-10 animate-spin text-green-500" />
            ) : hasError ? (
              <AlertCircle className={cn("h-10 w-10", getIconColor())} />
            ) : hasFile ? (
              <CheckCircle className={cn("h-10 w-10", getIconColor())} />
            ) : (
              <CloudUpload className={cn("h-10 w-10", getIconColor())} />
            )}
          </div>

          <Button
            className={cn(
              "transition-colors duration-200",
              getButtonColor(),
              isDisabled && "cursor-not-allowed",
            )}
            type="button"
            disabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled) handleClick();
            }}
          >
            {loading ? "Uploading..." : hasError ? "Try Again" : hasFile ? "Change File" : label}
          </Button>

          <div className="text-center">
            {file && !hasError && !loading ? (
              <div className="flex flex-col items-center justify-center space-y-1">
                <p className="text-sm font-medium text-green-700">{file.name}</p>
                <p className="text-xs text-green-600">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : loading ? (
              <p className="text-sm text-gray-500">Processing file...</p>
            ) : hasError ? (
              <p className="text-sm text-red-600">Please select a valid file</p>
            ) : (
              <p className="text-xs text-gray-500">{subtitle}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
